import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent } from 'react';
import { ImagePlus, Loader2, Send, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { BugStatusBoard } from '@/components/bugs/BugStatusBoard';
import {
  BUG_REPORT_PRIORITIES,
  currentReportUrl,
  newRequestId,
  prepareBugReportImage,
  type BugReportPriority,
} from '@/lib/bug-report';

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
}

type SendResponse = { success: boolean; error?: string; data?: { receipt_id: string | null; created: boolean } };

export function BugReportDialog({ open, onOpenChange, onSent }: BugReportDialogProps) {
  const user = useAuthStore((s) => s.user);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<BugReportPriority>('medium');
  const [link, setLink] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [processingImage, setProcessingImage] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'report' | 'board'>('report');
  const [categories, setCategories] = useState<{ key: string; label: string }[]>([]);
  const [category, setCategory] = useState('');
  const [metaReady, setMetaReady] = useState(false);
  const [metaError, setMetaError] = useState('');
  const [metaRevision, setMetaRevision] = useState(0);
  const submitting = useRef(false);
  // One id per report content: retries of unchanged input reuse it, so Kanban never creates duplicates.
  const [requestId, setRequestId] = useState(newRequestId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setRequestId(newRequestId());
  }, [title, message, priority, link, image, category]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setMetaReady(false); setMetaError('');
    void api.get<{ success: boolean; data?: { enabled: boolean; categories: { key: string; label: string }[] }; error?: string }>('/bug-reports/meta').then((res) => {
      if (!active) return;
      if (!res.success || !res.data?.enabled) throw new Error(res.error || 'Bugmeldungen sind nicht eingerichtet oder deaktiviert.');
      setCategories(res.data.categories);
      setCategory((current) => res.data!.categories.some((c) => c.key === current) ? current : '');
      setMetaReady(true);
    }).catch((e: unknown) => { if (active) setMetaError(e instanceof Error ? e.message : 'Kategorien konnten nicht geladen werden.'); });
    return () => { active = false; };
  }, [open, metaRevision]);

  useEffect(() => {
    if (!open) return;
    // Pristine form (first open or after a successful send): link to the page the user is on now.
    if (!title && !message && !image) setLink(currentReportUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!image) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const attachImage = async (file: File) => {
    if (submitting.current) return;
    setError(null);
    setProcessingImage(true);
    try {
      setImage(await prepareBugReportImage(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bild konnte nicht übernommen werden.');
    } finally {
      setProcessingImage(false);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'));
    if (!file) return;
    e.preventDefault();
    void attachImage(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (file) void attachImage(file);
  };

  const reset = () => {
    setTitle('');
    setMessage('');
    setPriority('medium');
    setCategory('');
    setImage(null);
    setError(null);
    setLink(currentReportUrl());
  };

  const canSubmit = metaReady && (!categories.length || categories.some((c) => c.key === category)) && title.trim().length > 0 && message.trim().length > 0 && !sending && !processingImage;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSubmit || submitting.current) return;
    submitting.current = true;
    setSending(true);
    setError(null);
    const form = new FormData();
    form.set('request_id', requestId);
    form.set('title', title.trim());
    form.set('message', message.trim());
    form.set('priority', priority);
    if (categories.length && category) form.set('category', category);
    if (link.trim()) form.set('url', link.trim());
    if (image) form.set('image', image, image.name);
    try {
      const res = await api.upload<SendResponse>('/bug-reports', form, { timeoutMs: 45000 });
      if (!res.success) throw new Error(res.error || 'Bugmeldung konnte nicht gesendet werden.');
      toast.success('Bugmeldung gesendet.');
      onSent?.();
      reset();
      onOpenChange(false);
    } catch (err) {
      // Inputs stay so the user can resend; the unchanged request id prevents a duplicate task.
      setError(err instanceof Error ? err.message : 'Bugmeldung konnte nicht gesendet werden.');
    } finally {
      setSending(false);
      submitting.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !sending && onOpenChange(next)}>
      <DialogContent className={cn('max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] overflow-y-auto', view === 'board' ? 'max-w-5xl' : 'max-w-xl')} onPaste={view === 'report' ? handlePaste : undefined}>
        <DialogHeader>
          <DialogTitle>{view === 'report' ? 'Bug melden' : 'Status der Bugmeldungen'}</DialogTitle>
          <DialogDescription>{view === 'report' ? 'Screenshot mit Strg+V einfügen oder Datei wählen.' : 'Der aktuelle Stand deiner Meldungen.'}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2"><Button variant={view === 'report' ? 'secondary' : 'outline'} disabled={sending} onClick={() => setView('report')}>Neue Meldung</Button><Button variant={view === 'board' ? 'secondary' : 'outline'} disabled={sending} onClick={() => setView('board')}>Meine Meldungen</Button></div>
        {view === 'board' ? <BugStatusBoard /> : <form
          onSubmit={submit}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') void submit();
          }}
          className="space-y-4"
        >
          <fieldset disabled={sending} className="space-y-4">
            {metaError && <div role="alert" className="space-y-2 text-sm text-danger"><p>{metaError}</p><Button type="button" variant="outline" onClick={() => setMetaRevision((v) => v + 1)}>Erneut laden</Button></div>}
            {!metaReady && !metaError && <p role="status" className="text-sm text-muted">Kategorien werden geladen…</p>}
            <div className="space-y-1.5">
              <Label htmlFor="bug-title">Titel</Label>
              <Input
                id="bug-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="bug-message">Beschreibung</Label>
              <Textarea
                id="bug-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={9500}
                rows={5}
                required
                placeholder="Was ist passiert, was hast du erwartet?"
              />
            </div>

            {!!categories.length && <div className="space-y-1.5"><Label htmlFor="bug-category">Kategorie</Label><Select value={category} onValueChange={setCategory} disabled={sending}><SelectTrigger id="bug-category"><SelectValue placeholder="Kategorie auswählen" /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></div>}
            <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="bug-priority">Priorität</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as BugReportPriority)} disabled={sending}>
                  <SelectTrigger id="bug-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUG_REPORT_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="bug-link">Link</Label>
                <Input id="bug-link" type="url" value={link} onChange={(e) => setLink(e.target.value)} maxLength={2048} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Screenshot</Label>
              {preview ? (
                <div className="relative overflow-hidden rounded-md border border-border bg-background">
                  <img src={preview} alt="Screenshot-Vorschau" className="max-h-56 w-full object-contain" />
                  <button
                    type="button"
                    onClick={() => setImage(null)}
                    aria-label="Screenshot entfernen"
                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted hover:bg-card-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  className={cn(
                    'flex h-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-sm text-muted transition-colors hover:bg-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    dragging && 'bg-card-hover text-foreground',
                  )}
                >
                  {processingImage ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                  <span>Strg+V, hierher ziehen oder klicken</span>
                  <span className="text-xs">PNG, JPG oder WebP · max. 5 MB</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void attachImage(file);
                }}
              />
            </div>
          </fieldset>

          <p className="text-xs text-muted">
            Gemeldet von <span className="font-medium text-foreground">{user?.full_name || user?.email || 'Unbekannt'}</span>
            {user?.full_name && user.email ? ` (${user.email})` : ''}
          </p>

          {error && (
            <div role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {sending ? <Loader2 className="animate-spin" /> : <Send />}
              {sending ? 'Senden…' : 'Senden'}
            </Button>
          </div>
        </form>}
      </DialogContent>
    </Dialog>
  );
}
